import { Injectable } from '@angular/core';
import { SharedService } from './sharedService';
import { ScreenshotTimeService } from './screenshot-time.service';

declare var $: any;

export interface ScreenshotFancyboxEmployee {
  name?: string;
  avatar?: string;
}

export interface ScreenshotFancyboxOpenOptions {
  items: any[];
  startIndex: number;
  userList?: any[];
  /** Employee avatar + name (Screenshots page). */
  showEmployee?: boolean;
  /** Window title line (Work Replay). */
  showWindow?: boolean;
  /** Employee context for Work Replay (page already has selected user). */
  employee?: ScreenshotFancyboxEmployee | null;
  /** Company shift timezone — same enrichment as Screenshots cards */
  shiftTimezone?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ScreenshotFancyboxService {
  private readonly closeControlClass = 'ss-fb-close-control';

  private readonly triggerChipClass: Record<string, string> = {
    'app-changed': 'screenshot-type-chip--appswitch',
    'admin-request': 'screenshot-type-chip--ondemand',
  };

  constructor(
    private sharedService: SharedService,
    private screenshotTime: ScreenshotTimeService
  ) {}

  open(options: ScreenshotFancyboxOpenOptions): void {
    const items = options?.items ?? [];
    if (typeof $ === 'undefined' || !$?.fancybox || !items.length) {
      return;
    }

    const ctx = {
      showEmployee: !!options.showEmployee,
      showWindow: !!options.showWindow,
      userList: options.userList ?? [],
      employee: options.employee ?? null,
      shiftTimezone: options.shiftTimezone ?? null,
    };

    const fancyItems = items
      .map((item) => {
        const src = item?.image_url || item?.large || item?.thumbnail || '';
        if (!src) {
          return null;
        }
        return {
          src,
          type: 'image',
          opts: {
            caption: this.buildCaptionHtml(item, ctx),
          },
        };
      })
      .filter(Boolean);

    if (!fancyItems.length) {
      return;
    }

    const index = Math.max(0, Math.min(options.startIndex ?? 0, fancyItems.length - 1));
    const layoutOpts = {
      slideShow: false,
      fullScreen: true,
      thumbs: false,
      infobar: true,
      buttons: true,
      closeBtn: true,
      margin: [32, 0, 0, 0],
      afterShow: () => {
        this.syncFooterMetrics();
        this.mountCloseControl();
      },
      onUpdate: (_e: unknown, slide: unknown) => this.fitImageInStage(slide),
      afterLoad: (_e: unknown, slide: unknown) => {
        this.hidePageOverlays();
        this.syncFooterMetrics();
        this.mountCloseControl();
        this.scheduleFitImage(slide);
      },
      afterMove: (_e: unknown, slide: unknown) => {
        this.hidePageOverlays();
        this.syncFooterMetrics();
        this.mountCloseControl();
        this.scheduleFitImage(slide);
      },
      afterClose: () => {
        this.unmountCloseControl();
        $(window).off('resize.ssFancybox', this.onResizeFit);
        $('html').removeClass('ss-fancybox-open');
      },
    };

    $('html').addClass('ss-fancybox-open');
    this.hidePageOverlays();
    $(window).on('resize.ssFancybox', this.onResizeFit);
    $.fancybox.open(fancyItems, { loop: true, baseClass: 'ss-fancybox', ...layoutOpts }, index);
  }

  /** Prominent top-right close — shared by Work Replay and Screenshots full-screen preview. */
  private mountCloseControl(): void {
    if (typeof $ === 'undefined') {
      return;
    }
    const $container = $('.ss-fancybox.fancybox-container').last();
    if (!$container.length || $container.find(`.${this.closeControlClass}`).length) {
      return;
    }

    const $btn = $(
      `<button type="button" class="${this.closeControlClass}" aria-label="Close preview" title="Close (Esc)">` +
        `<span class="material-symbols-outlined ${this.closeControlClass}-icon" aria-hidden="true">close</span>` +
        `<span class="${this.closeControlClass}-label">Close</span>` +
        `</button>`
    );
    $btn.on('click.ssFbClose', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      $.fancybox?.close?.();
    });
    $container.append($btn);
  }

  private unmountCloseControl(): void {
    if (typeof $ === 'undefined') {
      return;
    }
    $(`.${this.closeControlClass}`).off('click.ssFbClose').remove();
  }

  /** Only hide chart tooltips that sit above Fancybox — do not touch checklist or other app UI. */
  private hidePageOverlays(): void {
    if (typeof $ === 'undefined') {
      return;
    }
    $('#app_usage_tooltip_horizontal, #tooltipBarChart').hide();
  }

  private readonly onResizeFit = (): void => {
    this.syncFooterMetrics();
    const inst = $.fancybox?.getInstance?.();
    if (inst?.current) {
      this.fitImageInStage(inst.current);
    }
  };

  private syncFooterMetrics(): void {
    const $container = $('.ss-fancybox.fancybox-container');
    if (!$container.length) {
      return;
    }
    const $footer = $container.children('.fancybox-caption-wrap');
    const footerH = Math.ceil(($footer?.outerHeight?.() as number) || 44);
    $container.css('--ss-fb-footer-h', `${footerH}px`);
  }

  private scheduleFitImage(slide: unknown): void {
    const run = () => this.fitImageInStage(slide);
    run();
    requestAnimationFrame(run);
    setTimeout(run, 0);
    setTimeout(run, 80);
    const $slide = (slide as { $slide?: any })?.$slide;
    const $img = $slide?.find?.('.fancybox-image');
    if ($img?.length && !$img[0]?.complete) {
      $img.one('load', run);
    }
  }

  /** Fancybox sizes to slide height; size from viewport minus footer so the shot fills the screen. */
  private fitImageInStage(slide: unknown): void {
    this.syncFooterMetrics();
    const $slide = (slide as { $slide?: any })?.$slide;
    if (!$slide?.length) {
      return;
    }
    const $target = $slide.find('.fancybox-placeholder');
    const $img = $slide.find('.fancybox-image');
    if (!$img?.length) {
      return;
    }
    const el = $img[0] as HTMLImageElement;
    if (!el?.naturalWidth) {
      return;
    }
    const footerH =
      parseInt(
        String($('.ss-fancybox.fancybox-container').css('--ss-fb-footer-h') || '44'),
        10
      ) || 44;
    const toolbarH = 32;
    const maxW = Math.max(320, window.innerWidth - 4);
    const maxH = Math.max(240, window.innerHeight - footerH - toolbarH - 2);
    let w = el.naturalWidth;
    let h = el.naturalHeight;
    const ratio = Math.min(maxW / w, maxH / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
    const box = {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      transform: 'none',
      margin: '0 auto',
      display: 'block',
      width: `${w}px`,
      height: `${h}px`,
      maxWidth: `${maxW}px`,
      maxHeight: `${maxH}px`,
    };
    $img.css(box);
    if ($target?.length) {
      $target.css({ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center' });
    }
  }

  private buildCaptionHtml(
    item: any,
    ctx: {
      showEmployee: boolean;
      showWindow: boolean;
      userList: any[];
      employee: ScreenshotFancyboxEmployee | null;
      shiftTimezone: string | null;
    }
  ): string {
    const enriched = this.enrichItemTime(item, ctx.shiftTimezone);
    const chip = this.buildTypeChipHtml(enriched?.type);
    const appIcon = this.buildAppIconHtml(enriched);
    const appName = this.escapeHtml(String(enriched?.app_name || 'Unknown app').trim() || 'Unknown app');
    const timePrimary = this.escapeHtml(
      enriched?.timeDisplay?.primaryAbsolute || enriched?.captured_time || enriched?.ist_time || ''
    );
    const timeDetailsHtml = this.buildTimeDetailsHtml(enriched);

    const employeeHtml = ctx.showEmployee
      ? enriched?.user_id
        ? this.buildEmployeeHtml(enriched.user_id, ctx.userList)
        : ''
      : ctx.showWindow && ctx.employee?.name
        ? this.buildEmployeeFromContext(ctx.employee)
        : '';

    const timeInline = timePrimary
      ? `<span class="ss-fb-meta-sep" aria-hidden="true">·</span>` +
        `<span class="ss-fb-wr-time">${timePrimary}</span>`
      : '';
    const primaryRow = this.buildCompactPrimaryRow(
      employeeHtml,
      appIcon,
      appName,
      timeInline,
      chip
    );

    const windowHtml = ctx.showWindow ? this.buildWindowHtml(enriched) : '';

    return (
      `<div class="ss-fb-caption">` +
      `<div class="ss-fb-panel ss-fb-panel--compact">` +
      primaryRow +
      timeDetailsHtml +
      windowHtml +
      `</div>` +
      `</div>`
    );
  }

  private enrichItemTime(item: any, shiftTimezone: string | null): any {
    if (!item) {
      return item;
    }
    const raw =
      item.ist_time ?? item.captured_time ?? item.captured_at ?? item.utc_time ?? '';
    const tooltip = String(item?.timeDisplay?.tooltip || '');
    const hasCardTooltip =
      tooltip.includes('Your time:') &&
      (tooltip.includes('Office time:') || tooltip.includes('Saved as:'));
    if (hasCardTooltip && item.timeagoInput !== undefined) {
      return item;
    }
    const enriched = this.screenshotTime.getEnrichedScreenshotTime(raw, shiftTimezone);
    if (!enriched) {
      return item;
    }
    return { ...item, ...enriched };
  }

  /** Same lines as card hover tooltip, plus relative time when the card shows timeago. */
  private buildTimeDetailsHtml(item: any): string {
    const segments: string[] = [];

    if (item?.timeagoInput && item?.showAbsoluteTime === false) {
      const relative = this.formatRelativeCapture(item.timeagoInput);
      if (relative) {
        segments.push(relative);
      }
    }

    const tooltip = String(item?.timeDisplay?.tooltip || '').trim();
    if (tooltip) {
      for (const line of tooltip.split('\n')) {
        const text = line.trim();
        if (text && !segments.includes(text)) {
          segments.push(text);
        }
      }
    }

    if (!segments.length) {
      return '';
    }

    const inner = segments
      .map((s) => `<span class="ss-fb-time-detail">${this.escapeHtml(s)}</span>`)
      .join('<span class="ss-fb-meta-sep" aria-hidden="true">·</span>');

    return `<div class="ss-fb-time-details">${inner}</div>`;
  }

  private formatRelativeCapture(input: Date | string): string {
    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) {
      return '';
    }
    if (sec < 45) {
      return sec <= 1 ? 'just now' : `${sec} seconds ago`;
    }
    const min = Math.floor(sec / 60);
    if (min < 60) {
      return min === 1 ? '1 minute ago' : `${min} minutes ago`;
    }
    const hr = Math.floor(min / 60);
    if (hr < 24) {
      return hr === 1 ? '1 hour ago' : `${hr} hours ago`;
    }
    const day = Math.floor(hr / 24);
    return day === 1 ? '1 day ago' : `${day} days ago`;
  }

  private buildCompactPrimaryRow(
    employeeHtml: string,
    appIcon: string,
    appName: string,
    timeInline: string,
    chip: string
  ): string {
    return (
      `<div class="ss-fb-row-primary">` +
      employeeHtml +
      `<div class="ss-fb-app-bundle">` +
      appIcon +
      `<div class="ss-fb-app-meta">` +
      `<span class="ss-fb-wr-app-title">${appName}</span>` +
      timeInline +
      `</div>` +
      `</div>` +
      chip +
      `</div>`
    );
  }

  private buildEmployeeFromContext(employee: ScreenshotFancyboxEmployee): string {
    const name = this.escapeHtml(String(employee.name || 'Unknown User').trim() || 'Unknown User');
    const avatar = employee.avatar || '';
    const avatarHtml = avatar
      ? `<img class="ss-fb-employee-avatar" src="${this.escapeHtml(String(avatar))}" alt="" />`
      : `<span class="ss-fb-employee-initial" aria-hidden="true">${name.charAt(0) || '?'}</span>`;
    return (
      `<div class="ss-fb-employee ss-fb-employee--compact">` +
      avatarHtml +
      `<span class="ss-fb-employee-name">${name}</span>` +
      `</div>`
    );
  }

  private buildEmployeeHtml(userId: string | number, userList: any[]): string {
    const user = this.sharedService.getUserfromUserList(userId, userList);
    if (!user) {
      return '';
    }
    const name = this.escapeHtml(user.name || 'Unknown User');
    const avatar =
      user.profile_image_url || user.image_url || user.image || user.avatar || '';
    const initial = name.charAt(0) || '?';
    const avatarHtml = avatar
      ? `<img class="ss-fb-employee-avatar" src="${this.escapeHtml(String(avatar))}" alt="" />`
      : `<span class="ss-fb-employee-initial" aria-hidden="true">${initial}</span>`;
    return (
      `<div class="ss-fb-employee ss-fb-employee--compact">` +
      avatarHtml +
      `<span class="ss-fb-employee-name">${name}</span>` +
      `</div>`
    );
  }

  private buildWindowHtml(item: any): string {
    const c = item?._matchConfidence;
    const hasWindow =
      (c === 'exact_match' || c === 'near_match') && !!item?._windowName;
    if (!hasWindow) {
      return (
        '<div class="ss-fb-window ss-fb-window--footer ss-fb-window--compact ss-fb-window--muted">' +
        '<span class="ss-fb-window-label">Window</span>' +
        '<span class="ss-fb-window-value">Details unavailable</span>' +
        '</div>'
      );
    }
    const value = this.escapeHtml(item._windowName);
    return (
      `<div class="ss-fb-window ss-fb-window--footer ss-fb-window--compact" title="${value}">` +
      `<span class="ss-fb-window-label">Window</span>` +
      `<span class="ss-fb-window-value">${value}</span>` +
      `</div>`
    );
  }

  private buildTypeChipHtml(type: string | undefined | null): string {
    const key = (type || '').trim().toLowerCase();
    const cls = this.triggerChipClass[key] || 'screenshot-type-chip--periodic';
    let label = 'Periodic';
    let icon = 'schedule';
    if (key === 'app-changed') {
      label = 'App switch';
      icon = 'swap_horiz';
    } else if (key === 'admin-request') {
      label = 'On demand';
      icon = 'photo_camera';
    }
    return (
      `<span class="screenshot-type-chip ss-fb-chip ${cls}">` +
      `<span class="material-symbols-outlined ss-fb-chip-icon" aria-hidden="true">${icon}</span>` +
      `<span>${label}</span>` +
      `</span>`
    );
  }

  private buildAppIconHtml(item: any): string {
    const url = this.getAppIconSrc(item);
    if (url) {
      return `<span class="ss-fb-app-icon-wrap"><img class="ss-fb-app-icon-img" src="${this.escapeHtml(url)}" alt="" /></span>`;
    }
    const glyph = this.escapeHtml(this.getAppIconGlyph(item?.app_name));
    return (
      `<span class="ss-fb-app-icon-wrap">` +
      `<span class="material-symbols-outlined ss-fb-app-icon-fallback">${glyph}</span>` +
      `</span>`
    );
  }

  private getAppIconSrc(item: any): string | null {
    if (!item || item._appIconErr) {
      return null;
    }
    const u = item.app_icon_url || item.app_icon || item.application_icon_url;
    return typeof u === 'string' && u.length > 0 ? u : null;
  }

  private getAppIconGlyph(appName: string | undefined | null): string {
    const name = (appName || '').toLowerCase();
    if (/chrome|edge|firefox|safari|brave|opera/.test(name)) return 'language';
    if (/code|cursor|vscode|visual studio/.test(name)) return 'code';
    if (/slack|teams|discord|zoom|meet/.test(name)) return 'forum';
    if (/figma|sketch|photoshop|illustrator|premiere|adobe/.test(name)) return 'palette';
    if (/excel|word|powerpoint|outlook|office/.test(name)) return 'table_chart';
    if (/terminal|powershell|cmd|iterm|warp/.test(name)) return 'terminal';
    if (/explorer|finder|files?/.test(name)) return 'folder_open';
    return 'widgets';
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
